import { Component, HostListener } from '@angular/core';
import { NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

type NewsCategory = 'success' | 'product' | 'partnership' | 'event' | 'press';
type NewsFilter = NewsCategory | 'all';

interface NewsItem {
  category: NewsCategory;
  key: string; // i18n base key under landing.news.items
  sortDate: string; // ISO date, newest-first sort key — not shown to users (display date comes from i18n)
  image?: string; // shown on the card thumbnail + detail view; falls back to an icon if omitted
  isLogo?: boolean; // renders the image with object-fit: contain instead of cover, for partner/brand logos
  dark?: boolean; // pairs with isLogo — renders the logo tile on a dark background for light/white-on-transparent logos
  link?: string; // externally-hosted coverage (e.g. press): clicking the card navigates out instead of opening the detail modal
}

/** "Aktuelles / News" section. Add new items here; copy lives in i18n. */
@Component({
  selector: 'app-landing-news',
  standalone: true,
  imports: [NgForOf, NgIf, NgTemplateOutlet, TranslateModule],
  templateUrl: './landing-news.component.html',
  styleUrls: ['./landing-news.component.scss'],
})
export class LandingNewsComponent {
  readonly categories: NewsCategory[] = ['success', 'product', 'partnership', 'event', 'press'];

  readonly items: NewsItem[] = [
    { category: 'partnership', key: 'hlv', sortDate: '2025-01-01', image: 'assets/news/hlv-partnerschaft-hammamet.jpg' },
    { category: 'event', key: 'websummit_lisbon', sortDate: '2025-11-13', image: 'assets/news/websummit-lisbon-2025.jpg' },
    { category: 'partnership', key: 'unique', sortDate: '2026-05-01', image: 'assets/partners/unique.png', isLogo: true },
    { category: 'partnership', key: 'gmconnect', sortDate: '2026-06-01', image: 'assets/partners/gmconnect.png', isLogo: true, dark: true },
    { category: 'success', key: 'maher_visa', sortDate: '2026-06-10', image: 'assets/testimonials/maher.jpg' },
    { category: 'product', key: 'app_launch', sortDate: '2026-06-15', image: 'assets/new-logo-2.png', isLogo: true },
    { category: 'press', key: 'pnp_interview', sortDate: '2026-06-20', image: 'assets/news/pnp-interview-2026.jpg', link: 'https://www.pnp.de/21396713' },
    { category: 'partnership', key: 'millennia', sortDate: '2026-07-01', image: 'assets/news/millennia-partnerschaft.jpg' },
  ];

  activeFilter: NewsFilter = 'all';
  selected: NewsItem | null = null;

  get filteredItems(): NewsItem[] {
    const list = this.activeFilter === 'all' ? this.items : this.items.filter((item) => item.category === this.activeFilter);
    return [...list].sort((a, b) => b.sortDate.localeCompare(a.sortDate));
  }

  get secondaryItems(): NewsItem[] {
    return this.filteredItems.slice(1, 3);
  }

  get restItems(): NewsItem[] {
    return this.filteredItems.slice(3);
  }

  setFilter(filter: NewsFilter): void {
    this.activeFilter = filter;
  }

  openDetail(item: NewsItem): void {
    this.selected = item;
  }

  openLink(item: NewsItem): void {
    if (item.link) {
      window.open(item.link, '_blank', 'noopener,noreferrer');
    }
  }

  closeDetail(): void {
    this.selected = null;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeDetail();
  }
}
